import {
	Component,
	OnInit,
	OnChanges,
	SimpleChanges,
	OnDestroy,
	Input,
	Output,
	EventEmitter,
	ViewEncapsulation
} from '@angular/core';
import { IdService } from '../../shared/services/id.service';

@Component({
	selector: 'rbc-snackbar',
	templateUrl: './snackbar.component.html',
	styleUrls: ['./snackbar.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class SnackbarComponent implements OnInit, OnDestroy, OnChanges {
	@Input() id: string;
	@Input() active: boolean;
	@Input() buttonClose: string = 'Dismiss';
	@Input() message: string;
	@Input() positionX: 'left' | 'center' | 'right' = 'center';
	@Input() positionY: 'top' | 'bottom' = 'bottom';
	@Input() timeout: number = null;
	@Input() timeoutProgress: boolean;
	@Input() title: string;
	@Input() type: 'danger' | 'success';

	@Output() toggle = new EventEmitter();
	transition: string;
	iconClass: string;
	delay = null;
	progressAmount: number = 0;
	_animation;

	constructor(private idService: IdService) {}

	ngOnInit() {
		this.id = this.idService.generate();
		this.transition = `opacity-${this.transitionGen()}`;
		this.iconClass = `fa-${this.iconGen()}-circle`;
	}

	ngOnDestroy(): void {
		if (this.idService) {
			this.idService.remove(this.id);
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes) {
			if (changes.active) {
				// add toggle timeout if available and active
				if (this.active && this.timeout) {
					this.delay = setTimeout(() => {
						this.snackbar_toggle();
					}, this.timeout);
					// clear timeout if snackbar is not active
				} else if (!this.active) {
					clearTimeout(this.delay);
					this.delay = null;
				}

				// set progress bar
				if (this.active && this.timeoutProgress) {
					// reset animation
					this.progressAmount = 0;
					cancelAnimationFrame(this._animation);

					// grab current time
					const startTime = new Date().getTime();
					// grab end time based on current time and timeout
					const endTime = startTime + this.timeout;
					// start animation
					this.snackbar_progress(endTime);
				}
			}
		}
	}

	iconGen(): string {
		if (this.type === 'success') {
			return 'check';
		} else if (this.type === 'danger') {
			return 'exclamation';
		}
	}

	transitionGen(): string {
		if (this.positionX === 'left') {
			return 'left';
		} else if (this.positionX === 'right') {
			return 'right';
		} else if (this.positionY === 'top') {
			return 'down';
		} else {
			return 'up';
		}
	}

	classGen(): string[] {
		return [
			this.positionY ? `snackbar-${this.positionY}` : 'snackbar-bottom',
			this.positionX ? `snackbar-${this.positionX}` : 'snackbar-center',
			this.type ? `snackbar-${this.type}` : ''
		];
	}

	snackbar_toggle(e?: Event) {
		this.toggle.emit();
	}

	snackbar_progress(endTime: number) {
		// set animation step depending on current time to end time
		if (this.progressAmount < 100) {
			const currentTime = new Date().getTime();
			this.progressAmount = 100 - ((endTime - currentTime) / this.timeout) * 100;
			this._animation = requestAnimationFrame(this.snackbar_progress.bind(this, ...arguments));
			// stop animation when step reaches 100%
		} else {
			this.progressAmount = 100;
			cancelAnimationFrame(this._animation);
		}
	}

	snackbar_pause(e?: Event) {
		if (this.timeout) {
			cancelAnimationFrame(this._animation);
			clearTimeout(this.delay);
		}
	}

	snackbar_unpause(e?: Event) {
		if (this.timeout) {
			this.delay = setTimeout(() => {
				this.snackbar_toggle();
			}, this.timeout);
			const startTime = new Date().getTime();
			const endTime = startTime + this.timeout;
			this.snackbar_progress(endTime);
		}
	}
}
